import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { ApiError, api } from "@/lib/api";
import { useApiMutation } from "@/lib/use-api-mutation";
import { Link, createFileRoute, useLoaderData, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

/**
 * `/projects/:slug/releases/new` — create a release. The release
 * is created in GitLab (as an active milestone) on submit; flip
 * its status afterwards from the edit page.
 */

const INPUT_CLS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

export const Route = createFileRoute("/_app/projects/$slug/releases/new")({
  component: NewReleasePage,
});

function NewReleasePage() {
  const { t } = useTranslation();
  const { slug } = Route.useParams();
  const { project } = useLoaderData({ from: "/_app/projects/$slug" });
  const navigate = useNavigate();
  const [nextName, setNextName] = useState<string | null>(null);
  useEffect(() => {
    api
      .get<{ nextName: string | null }>(`/api/projects/${slug}/releases`)
      .then((data) => setNextName(data?.nextName ?? null))
      .catch(() => setNextName(null));
  }, [slug]);
  const create = useApiMutation(
    (body: { description: string | null; dueDate: string | null }) =>
      api.post(`/api/projects/${slug}/releases`, body),
    {
      onSuccess: () => navigate({ to: "/projects/$slug/releases", params: { slug } }),
      errorMessage: (err) =>
        err instanceof ApiError ? t("rel.new.errorForbidden") : t("rel.new.error"),
    },
  );

  async function createRelease(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    await create.mutate({
      description: ((fd.get("description") as string) ?? "").trim() || null,
      dueDate: ((fd.get("dueDate") as string) ?? "").trim() || null,
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-xl font-semibold">{t("rel.new.title")}</h2>
          <p className="text-muted-foreground text-sm mt-1">
            <Trans
              i18nKey="rel.new.addTo"
              values={{ project: project.name }}
              components={{
                link: (
                  <Link
                    to="/projects/$slug"
                    params={{ slug }}
                    className="text-primary hover:underline"
                  />
                ),
              }}
            />
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to="/projects/$slug/releases" params={{ slug }}>
            {t("rel.cancel")}
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t("rel.new.detailsTitle")}</CardTitle>
          <CardDescription>{t("rel.new.detailsDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={createRelease} className="space-y-4">
            <div className="space-y-2">
              <span className="text-sm font-medium leading-none">{t("rel.new.name")}</span>
              <p className="text-sm" data-testid="next-release-name">
                {nextName ?? "…"}
              </p>
              <p className="text-muted-foreground text-xs">{t("rel.new.nameHint")}</p>
            </div>

            <div className="space-y-2">
              <label htmlFor="description" className="text-sm font-medium leading-none">
                {t("rel.new.description")}
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                placeholder={t("rel.new.descriptionPlaceholder")}
                className={`${INPUT_CLS} min-h-[80px]`}
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="dueDate" className="text-sm font-medium leading-none">
                {t("rel.new.dueDate")}
              </label>
              <DatePicker id="dueDate" name="dueDate" />
            </div>

            {create.error && <p className="text-sm text-destructive">{create.error}</p>}

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={create.pending}>
                {create.pending ? t("rel.new.creating") : t("rel.new.create")}
              </Button>
              <Button variant="outline" type="button" asChild>
                <Link to="/projects/$slug/releases" params={{ slug }}>
                  {t("rel.cancel")}
                </Link>
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
